import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  Check,
  Coins,
  RefreshCw,
  ShieldCheck,
  Target,
  UserRound,
  Workflow,
  X,
} from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'


import { PageHeader, PageHeaderToolbar } from '@/components/app/PageHeader'
import { SortableTableHead, type SortDirection } from '@/components/app/SortableTableHead'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { KpiCard } from '@/features/dashboard/components/KpiCard'
import { useAuthSession } from '@/features/auth/session'
import { useAppBreadcrumbTrail } from '@/layouts/AppShell'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'
import { getAppLocale } from '@/lib/locale'

import { fetchProspect, fetchProspectHistory } from '../api'
import type { ProspectRecord, ProspectPipelineStage, ProspectConversionDisplayStatus, ProspectSubmissionStatus } from '@/types/prospects'

/* ── Presentation (badges only) ── */

const stageBadgeStyle: Record<string, string> = {
  suspect: 'border-transparent bg-muted text-muted-foreground',
  prospect_froid:
    'border-transparent bg-blue-500/15 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300',
  prospect_tiede:
    'border-transparent bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300',
  prospect_chaud:
    'border-transparent bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300',
}

function getStageLabel(t: (key: string) => string, stage: string): string {
  const labels: Record<string, string> = {
    suspect: t('prospects.stages.suspect'),
    prospect_froid: t('prospects.stages.prospect_froid'),
    prospect_tiede: t('prospects.stages.prospect_tiede'),
    prospect_chaud: t('prospects.stages.prospect_chaud'),
  }
  return labels[stage] ?? t('common.unknown')
}

const submissionBadgeStyle: Record<string, string> = {
  pending_sync:
    'border-transparent bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300',
  synced:
    'border-transparent bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300',
  sync_failed:
    'border-transparent bg-rose-500/15 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300',
  deleted: 'border-transparent bg-muted text-muted-foreground',
}

function getSubmissionLabel(t: (key: string) => string, status: string): string {
  const labels: Record<string, string> = {
    pending_sync: t('prospects.syncStatus.pending_sync'),
    synced: t('prospects.syncStatus.synced'),
    sync_failed: t('prospects.syncStatus.sync_failed'),
    deleted: t('prospects.syncStatus.deleted'),
  }
  return labels[status] ?? t('common.unknown')
}

const conversionBadgeStyle: Record<string, string> = {
  open: 'border-transparent bg-muted text-muted-foreground',
  converted:
    'border-transparent bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300',
  converted_invoice_pending:
    'border-transparent bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300',
  lost:
    'border-transparent bg-rose-500/15 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300',
  locked:
    'border-transparent bg-blue-500/15 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300',
}

function getConversionLabel(t: (key: string) => string, status: string): string {
  const labels: Record<string, string> = {
    open: t('prospects.conversionStatus.open'),
    converted: t('prospects.conversionStatus.converted'),
    converted_invoice_pending: t('prospects.conversionStatus.converted_invoice_pending'),
    lost: t('prospects.conversionStatus.lost'),
    locked: t('prospects.conversionStatus.locked'),
  }
  return labels[status] ?? t('common.unknown')
}

/* ── Helpers ── */

function formatDate(value: string | null, withTime = false) {
  if (!value) {
    return '—'
  }

  return new Date(value).toLocaleString(getAppLocale(), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(withTime
      ? {
          hour: '2-digit',
          minute: '2-digit',
        }
      : {}),
  })
}

function toTimestamp(value: string | null) {
  if (!value) return Number.NEGATIVE_INFINITY
  const parsed = new Date(value).getTime()
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed
}

/* ── Stepper ── */

type StepState = 'completed' | 'active' | 'pending' | 'error'

interface ProspectStep {
  key: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  date: string | null
  state: StepState
}

function buildAgentSteps(prospect: ProspectRecord, t: (key: string) => string): ProspectStep[] {
  // Step 1: Submitted
  const step1: ProspectStep = {
    key: 'submitted',
    label: t('prospects.detail.stepper.submitted'),
    icon: UserRound,
    date: prospect.submitted_at,
    state: 'completed',
  }

  // Step 2: Qualified (pipeline progression)
  let step2State: StepState = 'pending'
  if (prospect.pipeline_stage === 'prospect_chaud') step2State = 'completed'
  else if (prospect.pipeline_stage === 'prospect_tiede' || prospect.pipeline_stage === 'prospect_froid') {
    step2State = 'active'
  }

  const step2: ProspectStep = {
    key: 'qualified',
    label: t('prospects.detail.stepper.qualified'),
    icon: Target,
    date: prospect.pipeline_stage_changed_at,
    state: step2State,
  }

  // Step 3: Outcome
  let step3State: StepState = 'pending'
  let step3Date: string | null = null

  if (prospect.conversion_display_status === 'converted') {
    step3State = 'completed'
    step3Date = prospect.converted_at
  } else if (prospect.conversion_display_status === 'lost') {
    step3State = 'error'
    step3Date = prospect.lost_at
  } else if (prospect.conversion_display_status === 'locked') {
    step3State = 'active'
    step3Date = prospect.conversion_locked_at
  } else if (prospect.conversion_display_status === 'converted_invoice_pending') {
    step3State = 'active'
    step3Date = prospect.converted_at
  }

  const step3: ProspectStep = {
    key: 'outcome',
    label: t('prospects.detail.stepper.outcome'),
    icon: ShieldCheck,
    date: step3Date,
    state: step3State,
  }

  return [step1, step2, step3]
}

function buildOwnerSteps(prospect: ProspectRecord, t: (key: string) => string): ProspectStep[] {
  // Step 1: Submitted
  const step1: ProspectStep = {
    key: 'submitted',
    label: t('prospects.detail.stepper.submitted'),
    icon: UserRound,
    date: prospect.submitted_at,
    state: 'completed',
  }

  // Step 2: Synced
  let step2State: StepState = 'pending'
  if (prospect.submission_status === 'synced') step2State = 'completed'
  else if (prospect.submission_status === 'sync_failed') step2State = 'error'
  else if (prospect.submission_status === 'deleted') step2State = 'error'

  const step2: ProspectStep = {
    key: 'synced',
    label: t('prospects.detail.stepper.synced'),
    icon: RefreshCw,
    date: prospect.first_synced_at ?? prospect.last_synced_at,
    state: step2State,
  }

  // Step 3: Qualified (pipeline progression)
  let step3State: StepState = 'pending'
  if (prospect.pipeline_stage === 'prospect_chaud') step3State = 'completed'
  else if (prospect.pipeline_stage === 'prospect_tiede' || prospect.pipeline_stage === 'prospect_froid') {
    step3State = 'active'
  }

  const step3: ProspectStep = {
    key: 'qualified',
    label: t('prospects.detail.stepper.qualified'),
    icon: Target,
    date: prospect.pipeline_stage_changed_at,
    state: step3State,
  }

  // Step 4: Outcome
  let step4State: StepState = 'pending'
  let step4Date: string | null = null

  if (prospect.conversion_display_status === 'converted') {
    step4State = 'completed'
    step4Date = prospect.converted_at
  } else if (prospect.conversion_display_status === 'lost') {
    step4State = 'error'
    step4Date = prospect.lost_at
  } else if (prospect.conversion_display_status === 'locked') {
    step4State = 'active'
    step4Date = prospect.conversion_locked_at
  } else if (prospect.conversion_display_status === 'converted_invoice_pending') {
    step4State = 'active'
    step4Date = prospect.converted_at
  }

  const step4: ProspectStep = {
    key: 'outcome',
    label: t('prospects.detail.stepper.outcome'),
    icon: ShieldCheck,
    date: step4Date,
    state: step4State,
  }

  return [step1, step2, step3, step4]
}

function ProspectStatusStepper({ steps }: { steps: ProspectStep[] }) {
  return (
    <div className="relative">
      <div className="flex items-start justify-between gap-2">
        {steps.map((step, i) => (
          <div key={step.key} className="relative flex flex-1 flex-col items-center">
            {i > 0 && (
              <div
                className={cn(
                  'absolute right-1/2 top-5 h-0.5 w-full -translate-y-1/2',
                  steps[i - 1].state === 'completed' || steps[i - 1].state === 'active'
                    ? 'bg-emerald-500 dark:bg-emerald-400'
                    : 'bg-border',
                )}
              />
            )}

            <div
              className={cn(
                'relative z-10 flex size-10 items-center justify-center rounded-full text-sm',
                step.state === 'completed' && 'bg-emerald-500 text-white dark:bg-emerald-400',
                step.state === 'active' && 'bg-blue-500 text-white dark:bg-blue-400',
                step.state === 'pending' && 'border-2 border-border bg-background text-muted-foreground',
                step.state === 'error' && 'bg-rose-500 text-white dark:bg-rose-400',
              )}
            >
              {step.state === 'completed' ? (
                <Check className="size-5" />
              ) : step.state === 'error' ? (
                <X className="size-5" />
              ) : (
                <step.icon className="size-5" />
              )}
            </div>

            <p
              className={cn(
                'mt-3 text-center text-sm font-semibold',
                step.state === 'completed' || step.state === 'active'
                  ? 'text-foreground'
                  : step.state === 'error'
                    ? 'text-rose-600 dark:text-rose-400'
                    : 'text-muted-foreground',
              )}
            >
              {step.label}
            </p>

            {step.date ? (
              <p className="mt-1 text-center text-xs text-muted-foreground">
                {formatDate(step.date, true)}
              </p>
            ) : (
              <p className="mt-1 text-center text-xs text-muted-foreground/50">—</p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

/* ── KPI tone helpers ── */

function stageTone(stage: ProspectPipelineStage): 'primary' | 'success' | 'warning' | 'info' | 'danger' {
  switch (stage) {
    case 'prospect_chaud':
      return 'success'
    case 'prospect_tiede':
      return 'warning'
    case 'prospect_froid':
    case 'suspect':
    default:
      return 'info'
  }
}

function conversionTone(status: ProspectConversionDisplayStatus): 'primary' | 'success' | 'warning' | 'info' | 'danger' {
  switch (status) {
    case 'converted':
      return 'success'
    case 'converted_invoice_pending':
      return 'warning'
    case 'lost':
      return 'danger'
    case 'locked':
      return 'primary'
    case 'open':
    default:
      return 'info'
  }
}

function submissionTone(status: ProspectSubmissionStatus): 'primary' | 'success' | 'warning' | 'info' | 'danger' {
  switch (status) {
    case 'synced':
      return 'success'
    case 'pending_sync':
      return 'warning'
    case 'sync_failed':
    case 'deleted':
      return 'danger'
    default:
      return 'info'
  }
}

/* ── Skeleton ── */

function ProspectDetailSkeleton({ isAgentView }: { isAgentView: boolean }) {
  const stepCount = isAgentView ? 3 : 4

  return (
    <section className="app-section">
      {/* Header */}
      <div className="flex flex-col gap-2 pb-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-8 rounded-lg" />
          <Skeleton className="h-6 w-48 rounded-md" />
          <Skeleton className="h-5 w-20 rounded-full" />
          {!isAgentView && (
            <>
              <Skeleton className="h-5 w-20 rounded-full" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {!isAgentView && <Skeleton className="h-8 w-28 rounded-md" />}
          <Skeleton className="h-8 w-28 rounded-md" />
        </div>
      </div>

      {/* KPI Cards */}
      <div className={`grid gap-3 ${isAgentView ? 'sm:grid-cols-2' : 'md:grid-cols-2 xl:grid-cols-4'}`}>
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="rounded-lg bg-card p-4 md:p-6">
            <div className="flex items-center gap-2.5">
              <Skeleton className="size-8 rounded-md" />
              <Skeleton className="h-4 w-24" />
            </div>
            <Skeleton className="mt-4 h-8 w-28" />
            <Skeleton className="mt-4 h-3.5 w-36" />
          </div>
        ))}
      </div>

      {/* Status Stepper */}
      <div className="rounded-lg bg-card app-card-padding">
        <Skeleton className="mb-6 h-5 w-48 rounded-md" />
        <div className="flex items-start justify-between gap-2">
          {Array.from({ length: stepCount }).map((_, index) => (
            <div key={index} className="flex flex-1 flex-col items-center">
              <Skeleton className="size-10 rounded-full" />
              <Skeleton className="mt-3 h-3 w-20" />
              <Skeleton className="mt-1 h-2.5 w-24" />
            </div>
          ))}
        </div>
      </div>

      {/* Owner: Sync Table */}
      {!isAgentView && (
        <div className="rounded-lg bg-card app-card-padding">
          <div className="mb-5 flex items-start justify-between">
            <div className="space-y-2">
              <Skeleton className="h-5 w-36 rounded-md" />
              <Skeleton className="h-4 w-48 rounded-md" />
            </div>
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          <div className="overflow-hidden rounded-lg border border-border">
            <div className="h-10 bg-muted/30" />
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className={cn(
                  'flex items-center gap-3 px-4 py-3',
                  index % 2 === 1 && 'bg-muted/15',
                  index > 0 && 'border-t border-border/50',
                )}
              >
                <Skeleton className="h-4 w-28 rounded-md" />
                <Skeleton className="h-4 w-32 rounded-md" />
                <Skeleton className="h-5 w-20 rounded-full" />
                <Skeleton className="ml-auto h-4 w-24 rounded-md" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History Table */}
      {!isAgentView && (
        <div className="rounded-lg bg-card app-card-padding">
          <div className="mb-5 flex items-start justify-between">
            <div className="space-y-2">
              <Skeleton className="h-5 w-32 rounded-md" />
              <Skeleton className="h-4 w-56 rounded-md" />
            </div>
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          <div className="overflow-hidden rounded-lg border border-border">
            <div className="h-10 bg-muted/30" />
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className={cn(
                  'flex items-center gap-3 px-4 py-3',
                  index % 2 === 1 && 'bg-muted/15',
                  index > 0 && 'border-t border-border/50',
                )}
              >
                <Skeleton className="h-4 w-28 rounded-md" />
                <Skeleton className="h-4 w-20 rounded-md" />
                <Skeleton className="h-4 w-24 rounded-md" />
                <Skeleton className="h-4 w-24 rounded-md" />
                <Skeleton className="h-4 w-20 rounded-md" />
                <Skeleton className="h-4 w-40 rounded-md" />
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

type SyncSortKey = 'updatedAt'
type HistorySortKey = 'createdAt'

export function ProspectDetailPage() {
  const { t } = useTranslation()
  const { user } = useAuthSession()
  const isAgentView = Boolean(user?.agent_profile)
  const { prospectId } = useParams<{
    prospectId: string
  }>()
  const [syncSortKey, setSyncSortKey] = useState<SyncSortKey>('updatedAt')
  const [syncSortDirection, setSyncSortDirection] = useState<SortDirection>('desc')
  const [historySortKey, setHistorySortKey] = useState<HistorySortKey>('createdAt')
  const [historySortDirection, setHistorySortDirection] = useState<SortDirection>('desc')

  const prospectQuery = useQuery({
    queryKey: ['prospects', 'detail', prospectId],
    queryFn: async () => fetchProspect(prospectId ?? ''),
    enabled: Boolean(prospectId),
  })

  const historyQuery = useQuery({
    queryKey: ['prospects', 'detail', prospectId, 'history'],
    queryFn: async () => fetchProspectHistory(prospectId ?? ''),
    enabled: Boolean(prospectId),
  })

  const prospect = prospectQuery.data?.data ?? null
  const history = historyQuery.data?.data ?? []
  const resolvedAgentId = prospect?.agent_id ?? null

  useAppBreadcrumbTrail(
    prospect
      ? [
          { label: t('navigation.prospects'), to: '/prospects' },
          { label: prospect.contact_name },
        ]
      : null,
  )

  const stageStyle = prospect ? stageBadgeStyle[prospect.pipeline_stage] : undefined
  const stageLabel = prospect ? getStageLabel(t, prospect.pipeline_stage) : undefined
  const submissionStyle = prospect ? submissionBadgeStyle[prospect.submission_status] : undefined
  const submissionLabel = prospect ? getSubmissionLabel(t, prospect.submission_status) : undefined
  const conversionStyle = prospect ? conversionBadgeStyle[prospect.conversion_display_status] : undefined
  const conversionLabel = prospect ? getConversionLabel(t, prospect.conversion_display_status) : undefined
  const backHref = '/prospects'

  const steps = useMemo(() => {
    if (!prospect) return []
    return isAgentView
      ? buildAgentSteps(prospect, t)
      : buildOwnerSteps(prospect, t)
  }, [prospect, isAgentView, t])

  const syncRows = useMemo(
    () => [
      {
        key: 'submission',
        element: t('prospects.detail.sync.row.submission'),
        value: submissionLabel ?? t('common.notAvailable'),
        status: submissionStyle ? <Badge className={submissionStyle}>{submissionLabel}</Badge> : null,
        updatedAtValue: prospect?.submitted_at ?? null,
        updatedAtLabel: formatDate(prospect?.submitted_at ?? null, true),
      },
      {
        key: 'iacrm-sync',
        element: t('prospects.detail.sync.row.iacrmSync'),
        value: prospect?.iacrm_status_label ?? t('prospects.detail.sync.row.noStatus'),
        status: submissionStyle ? <Badge className={submissionStyle}>{submissionLabel}</Badge> : null,
        updatedAtValue: prospect?.last_synced_at ?? null,
        updatedAtLabel: formatDate(prospect?.last_synced_at ?? null, true),
      },
      {
        key: 'iacrm-reference',
        element: t('prospects.detail.sync.row.iacrmRef'),
        value: prospect?.iacrm_prospect_id ?? t('prospects.detail.sync.row.notAssigned'),
        status: (
          <Badge variant="secondary">{prospect?.iacrm_prospect_id ? t('prospects.detail.sync.row.linked') : t('prospects.detail.sync.row.pending')}</Badge>
        ),
        updatedAtValue: prospect?.last_synced_at ?? null,
        updatedAtLabel: formatDate(prospect?.last_synced_at ?? null, true),
      },
      {
        key: 'conversion',
        element: t('prospects.detail.sync.row.conversion'),
        value: conversionLabel ?? t('common.notAvailable'),
        status: conversionStyle ? <Badge className={conversionStyle}>{conversionLabel}</Badge> : null,
        updatedAtValue:
          prospect?.converted_at ??
          prospect?.lost_at ??
          prospect?.conversion_locked_at ??
          prospect?.pipeline_stage_changed_at ??
          null,
        updatedAtLabel: formatDate(
          prospect?.converted_at ??
            prospect?.lost_at ??
            prospect?.conversion_locked_at ??
            prospect?.pipeline_stage_changed_at ??
            null,
          true,
        ),
      },
      {
        key: 'lock',
        element: t('prospects.detail.sync.row.lock'),
        value: prospect?.conversion_locked_at ? t('prospects.detail.sync.row.locked') : t('prospects.detail.sync.row.open'),
        status: (
          <Badge
            className={
              prospect?.conversion_locked_at
                ? conversionBadgeStyle.locked
                : 'border-transparent bg-muted text-muted-foreground'
            }
          >
            {prospect?.conversion_locked_at ? t('prospects.detail.sync.row.locked') : t('prospects.detail.sync.row.open')}
          </Badge>
        ),
        updatedAtValue: prospect?.conversion_locked_at ?? null,
        updatedAtLabel: formatDate(prospect?.conversion_locked_at ?? null, true),
      },
      {
        key: 'deletion',
        element: t('prospects.detail.sync.row.deletion'),
        value: prospect?.deleted_at
          ? prospect.soft_delete_reason ?? t('prospects.detail.sync.row.deletedNoReason')
          : t('prospects.detail.sync.row.active'),
        status: (
          <Badge
            className={
              prospect?.deleted_at
                ? submissionBadgeStyle.deleted
                : 'border-transparent bg-muted text-muted-foreground'
            }
          >
            {prospect?.deleted_at ? t('prospects.detail.sync.row.deleted') : t('prospects.detail.sync.row.active')}
          </Badge>
        ),
        updatedAtValue: prospect?.deleted_at ?? null,
        updatedAtLabel: formatDate(prospect?.deleted_at ?? null, true),
      },
    ],
    [conversionLabel, conversionStyle, prospect, submissionLabel, submissionStyle, stageLabel, stageStyle, t],
  )

  const sortedSyncRows = useMemo(() => {
    const rows = [...syncRows]
    if (syncSortKey === 'updatedAt') {
      rows.sort((left, right) => {
        const delta = toTimestamp(left.updatedAtValue) - toTimestamp(right.updatedAtValue)
        return syncSortDirection === 'desc' ? -delta : delta
      })
    }
    return rows
  }, [syncRows, syncSortDirection, syncSortKey])

  const historyRows = useMemo(
    () =>
      history.map((event) => ({
        id: event.id,
        createdAtValue: event.created_at,
        createdAtLabel: formatDate(event.created_at, true),
        sourceSystem: event.source_system,
        progression: event.new_progression_status ?? t('prospects.detail.history.noProgression'),
        submissionStatus: event.new_submission_status ?? t('prospects.detail.history.noSubmission'),
        author: event.changed_by_user?.display_name ?? t('prospects.detail.history.system'),
        detail: event.reason ?? t('prospects.detail.history.noComment'),
      })),
    [history, t],
  )

  const sortedHistoryRows = useMemo(() => {
    const rows = [...historyRows]
    if (historySortKey === 'createdAt') {
      rows.sort((left, right) => {
        const delta = toTimestamp(left.createdAtValue) - toTimestamp(right.createdAtValue)
        return historySortDirection === 'desc' ? -delta : delta
      })
    }
    return rows
  }, [historyRows, historySortDirection, historySortKey])

  function handleSyncSort(nextKey: SyncSortKey) {
    if (syncSortKey === nextKey) {
      setSyncSortDirection((current) => (current === 'desc' ? 'asc' : 'desc'))
      return
    }
    setSyncSortKey(nextKey)
    setSyncSortDirection('desc')
  }

  function handleHistorySort(nextKey: HistorySortKey) {
    if (historySortKey === nextKey) {
      setHistorySortDirection((current) => (current === 'desc' ? 'asc' : 'desc'))
      return
    }
    setHistorySortKey(nextKey)
    setHistorySortDirection('desc')
  }

  if (!prospectId) {
    return (
      <article className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {t('prospects.detail.error.missingId')}
      </article>
    )
  }

  if (prospectQuery.isPending || historyQuery.isPending) {
    return <ProspectDetailSkeleton isAgentView={isAgentView} />
  }

  if (prospectQuery.isError) {
    return (
      <article className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {(prospectQuery.error as ApiError).message}
      </article>
    )
  }

  if (historyQuery.isError) {
    return (
      <article className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {(historyQuery.error as ApiError).message}
      </article>
    )
  }

  if (!prospect) {
    return (
      <article className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {t('prospects.detail.error.notFound')}
      </article>
    )
  }

  return (
    <section className="app-section">
      {/* ── Header ── */}
      <PageHeader
        beforeTitle={
          <Button asChild variant="ghost" size="icon-sm" className="shrink-0">
            <Link to={backHref} aria-label={t('prospects.detail.back')}>
              <ArrowLeft className="size-4" />
            </Link>
          </Button>
        }
        title={prospect.contact_name}
        titleAddon={
          <>
            <Badge className={stageStyle}>{stageLabel ?? ''}</Badge>
            <Badge className={conversionStyle}>{conversionLabel ?? ''}</Badge>
            {!isAgentView && <Badge className={submissionStyle}>{submissionLabel ?? ''}</Badge>}
          </>
        }
        right={
          <PageHeaderToolbar>
            {!isAgentView && resolvedAgentId ? (
              <Button asChild variant="outline" size="sm">
                <Link to={`/agents/${resolvedAgentId}`}>
                  <UserRound className="mr-2 h-4 w-4" />
                  {t('prospects.detail.viewAgent')}
                </Link>
              </Button>
            ) : null}
            {prospect.program_id ? (
              <Button asChild variant="outline" size="sm">
                <Link to={`/programs/${prospect.program_id}`}>
                  <Workflow className="mr-2 h-4 w-4" />
                  {t('prospects.detail.viewProgram')}
                </Link>
              </Button>
            ) : null}
          </PageHeaderToolbar>
        }
      />

      {/* ── KPI Cards ── */}
      <div className={`grid gap-3 ${isAgentView ? 'sm:grid-cols-2' : 'md:grid-cols-2 xl:grid-cols-4'}`}>
        {isAgentView ? (
          <>
            <KpiCard
              title={t('prospects.detail.kpi.contact.title')}
              value={prospect.contact_name}
              description={prospect.contact_email ?? prospect.contact_phone_raw ?? t('prospects.contact.noEmail')}
              icon={UserRound}
              tone="info"
            />
            <KpiCard
              title={t('prospects.detail.kpi.program.title')}
              value={prospect.program_name ?? t('prospects.detail.contact.noProgram')}
              description={t('prospects.detail.kpi.program.description')}
              icon={Workflow}
              tone="success"
            />
            <KpiCard
              title={t('prospects.detail.kpi.stage.title')}
              value={stageLabel ?? t('common.unknown')}
              description={t('prospects.detail.kpi.stage.description')}
              icon={Target}
              tone={stageTone(prospect.pipeline_stage)}
            />
            <KpiCard
              title={t('prospects.detail.kpi.pointsEarned.title')}
              value={
                prospect.earned_points === null
                  ? prospect.projected_points === null
                    ? t('prospects.detail.kpi.pointsEarned.pending')
                    : `${prospect.projected_points.toLocaleString(getAppLocale())} pts`
                  : `${prospect.earned_points.toLocaleString(getAppLocale())} pts`
              }
              description={
                prospect.earned_points === null
                  ? t('prospects.detail.kpi.pointsProjected.description')
                  : t('prospects.detail.kpi.pointsEarned.description')
              }
              icon={Coins}
              tone="primary"
            />
          </>
        ) : (
          <>
            <KpiCard
              title={t('prospects.detail.kpi.stage.title')}
              value={stageLabel ?? t('common.unknown')}
              description={t('prospects.detail.kpi.stage.description')}
              icon={Target}
              tone={stageTone(prospect.pipeline_stage)}
            />
            <KpiCard
              title={t('prospects.detail.kpi.conversion.title')}
              value={conversionLabel ?? t('common.unknown')}
              description={t('prospects.detail.kpi.conversion.description')}
              icon={ShieldCheck}
              tone={conversionTone(prospect.conversion_display_status)}
            />
            <KpiCard
              title={t('prospects.detail.kpi.agent.title')}
              value={prospect.agent_name ?? t('prospects.table.unknownAgent')}
              description={t('prospects.detail.kpi.agent.description')}
              icon={UserRound}
              tone="info"
            />
            <KpiCard
              title={t('prospects.detail.kpi.submission.title')}
              value={submissionLabel ?? t('common.unknown')}
              description={t('prospects.detail.kpi.submission.description')}
              icon={RefreshCw}
              tone={submissionTone(prospect.submission_status)}
            />
          </>
        )}
      </div>

      {/* ── Status Stepper ── */}
      <div className="rounded-lg bg-card app-card-padding">
        <div className="mb-6">
          <h3 className="text-base font-semibold text-foreground">{t('prospects.detail.timeline.title')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t('prospects.detail.timeline.description')}</p>
        </div>
        <ProspectStatusStepper steps={steps} />
      </div>

      {/* ── Owner: Sync State Table ── */}
      {!isAgentView && (
        <Card className="border-0 shadow-none">
          <CardHeader className="pb-0">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle>{t('prospects.detail.sync.title')}</CardTitle>
                <CardDescription>{t('prospects.detail.sync.description')}</CardDescription>
              </div>
              <Badge variant="secondary">{t('prospects.detail.sync.markers', { count: syncRows.length })}</Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-5">
            <div className="overflow-hidden rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                    <TableHead className="text-xs font-semibold">{t('prospects.detail.sync.table.element')}</TableHead>
                    <TableHead className="text-xs font-semibold">{t('prospects.detail.sync.table.value')}</TableHead>
                    <TableHead className="text-xs font-semibold">{t('prospects.detail.sync.table.status')}</TableHead>
                    <SortableTableHead
                      sortKey="updatedAt"
                      activeKey={syncSortKey}
                      direction={syncSortDirection}
                      onSort={handleSyncSort}
                      className="text-right text-xs font-semibold"
                      align="right"
                    >
                      {t('prospects.detail.sync.table.lastUpdate')}
                    </SortableTableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedSyncRows.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell className="font-medium text-foreground">{row.element}</TableCell>
                      <TableCell className="max-w-[24rem] whitespace-normal text-sm text-muted-foreground">
                        {row.value}
                      </TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                        {row.updatedAtLabel}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── History Table (owner only) ── */}
      {!isAgentView && (
        <Card className="border-0 shadow-none">
        <CardHeader className="pb-0">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>{t('prospects.detail.history.title')}</CardTitle>
              <CardDescription>{t('prospects.detail.history.description')}</CardDescription>
            </div>
            <Badge variant="secondary">{t('prospects.detail.history.events', { count: history.length })}</Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-5">
          {sortedHistoryRows.length === 0 ? (
            <div className="rounded-lg bg-muted/20 py-8 text-center text-sm text-muted-foreground">
              {t('prospects.detail.history.empty')}
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                    <SortableTableHead
                      sortKey="createdAt"
                      activeKey={historySortKey}
                      direction={historySortDirection}
                      onSort={handleHistorySort}
                      className="text-xs font-semibold"
                    >
                      {t('prospects.detail.history.table.date')}
                    </SortableTableHead>
                    <TableHead className="text-xs font-semibold">{t('prospects.detail.history.table.system')}</TableHead>
                    <TableHead className="text-xs font-semibold">{t('prospects.detail.history.table.progression')}</TableHead>
                    <TableHead className="text-xs font-semibold">{t('prospects.detail.history.table.submission')}</TableHead>
                    <TableHead className="text-xs font-semibold">{t('prospects.detail.history.table.author')}</TableHead>
                    <TableHead className="min-w-[18rem] text-xs font-semibold">{t('prospects.detail.history.table.detail')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedHistoryRows.map((row, index) => (
                    <TableRow key={row.id} className={cn(index % 2 === 1 && 'bg-muted/15')}>
                      <TableCell className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
                        {row.createdAtLabel}
                      </TableCell>
                      <TableCell className="uppercase tracking-[0.12em] text-muted-foreground">
                        {row.sourceSystem}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{row.progression}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{row.submissionStatus}</Badge>
                      </TableCell>
                      <TableCell className="text-foreground">{row.author}</TableCell>
                      <TableCell className="whitespace-normal text-muted-foreground">
                        {row.detail}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </section>
  )
}
